import {objectPropertiesToArray} from "../utils/utils";
import {FunctionComponent, lazy} from "react";

interface IComponentType extends Object {
    name: String,
    dashboardComponent: boolean,
    invisible?: boolean,
    entryPoint: Function,
    editorEntryPoint?: FunctionComponent<ICommonComponentEditorProps>,
    editorConfig?: EditorConfig
}


export type ComponentTypeWithID = IComponentType & IdType

type EditorConfig = {
    hasTitle: boolean
}

export interface ICommonComponentEditorProps {
    config: any,
    onConfigChange: Function
}

interface IComponentTypeMappings {
    [key: string]: IComponentType
}

export const ComponentTypeMappings: IComponentTypeMappings = {
    // ========= Dashboard Components ==============
    SparkLineValueIndicator: {
        name: "Spark Line Value Indicator",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/sparkline/SparkLine" /* webpackChunkName: "spark-line" */),
        editorEntryPoint : lazy(() => import("./dashboard/sparkline/SparkLineEditor") /* webpackChunkName: "spark-line-editor" */)
    },
    MeasurementDeviceTableAgreg: {
        name: "Measurement Device Table Aggregated",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/measurement-device-table-agreg/MeasurementDeviceTableAgreg" /* webpackChunkName: "measurement-device-table-agreg" */)
    },
    DatasetIndicator: {
        name: "Dataset Gauge Indicator",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/dataset-indicator/DatasetIndicator" /* webpackChunkName: "dataset-indicator" */)
    },
    ProductTemplateTable: {
        name: "Product Template Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/product-template-table/ProductTemplateTable" /* webpackChunkName: "product-template-table" */)
    },
    DatasetLineChart: {
        name: "Dataset Line Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/DatasetLineChart" /* webpackChunkName: "dataset-line-chart" */)
    },
    DatasetHistogramChart: {
        name: "Dataset Histogram Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/HistogramChart" /* webpackChunkName: "dataset-histogram-chart" */)
    },
    DatasetBarChart: {
        name: "Dataset Bar Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/bar-chart/DatasetBarChartRenderer" /* webpackChunkName: "dataset-bar-chart-renderer" */)
    },
    DatasetScatterChart: {
        name: "Dataset Scatter Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/DatasetScatterChart" /* webpackChunkName: "dataset-scatter-chart" */)
    },
    DatasetPieChart: {
        name: "Dataset Pie Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/DatasetPieChart" /* webpackChunkName: "dataset-pie-chart" */)
    },
    DatasetSankeyChart: {
        name: "Dataset Sankey Chart",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/charts/PlotlySankey/Sankey" /* webpackChunkName: "dataset-sankey-chart" */)
    },
    DataInputOutputTable: {
        name: "Data Input Output Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/data-input-output-table/main-table/DataInputOutputTable" /* webpackChunkName: "data-input-output-table" */)
    },
    Scada: {
        name: "SCADA",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/scada/Scada" /* webpackChunkName: "scada" */)
    },
    DatasetToggleTable: {
        name: "Dataset Toggle Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/dataset-toggle-table/DatasetToggleTable" /* webpackChunkName: "dataset-toggle-table" */)
    },
    DatasetSetpointTable: {
        name: "Dataset Setpoint Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/dataset-setpoint-table/DatasetSetpointTable" /* webpackChunkName: "dataset-setpoint-table" */)
    },
    DatasetTable: {
        name: "Dataset Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/dataset-table/DatasetTable" /* webpackChunkName: "dataset-table" */)
    },
    EapsmLicenceManager: {
        name: "Eapsm License Manager",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/eapsm-license-manager/EapsmLicenseManager" /* webpackChunkName: "eapsm-license-manager" */)
    },
    FreeText: {
        name: "Free Text",
        dashboardComponent: true,
        entryPoint: () => import("./pages/dashboard/components/free-text/FreeText" /* webpackChunkName: "free-text" */)
    },
    GeneratedReportTable: {
        name: "Generated Report Table",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/generated-report-table/GeneratedReportTable" /* webpackChunkName: "generated-report-table" */)
    },
    AlertFan: {
        name: "Alert Fan",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/alert-fan/AlertTabMain" /* webpackChunkName: "alert-fan-tab" */)
    },
    Separator: {
        name: "Separator",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/separator/Separator" /* webpackChunkName: "separator" */),
        editorEntryPoint: lazy(() => import("./dashboard/separator/SeparatorEditor") /* webpackChunkName: "separator-editor" */)
    },
    // ==================== Page Fragments ====================
    DashboardComponentConfigurator: {
        name: "Dashboard Component Configurator",
        dashboardComponent: false,
        entryPoint: () => import("./fragments/DashboardComponentConfigurator" /* webpackChunkName: "dashboard-component-configurator" */)
    },
    ProductTemplateConfiguratorJson: {
        name: "Product Template Configurator (JSON)",
        dashboardComponent: false,
        entryPoint: () => import("./fragments/ProductTemplatesConfiguratorJson" /* webpackChunkName: "prod-templates-configurator" */)
    },
    DataWindowChart: {
        name: "Data Window Chart",
        dashboardComponent: false,
        entryPoint: () => import("./fragments/DataWindowChart" /* webpackChunkName: "data-window-chart" */)
    },
    ProductTemplateDataManagerFragment: {
        name: "Product Template Data Manager",
        dashboardComponent: false,
        entryPoint: () => import("./fragments/ProductTemplateDataManagerFragment" /* webpackChunkName: "product-template-data-manager" */)
    },

    EasasGlobalFragment: {
        name: "Global Fragment",
        dashboardComponent: false,
        invisible: true,
        entryPoint: () => import("./fragments/easas-global-fragment/EasasGlobalFragment" /* webpackChunkName: "product-template-data-manager" */)
    },
    // ==================== Pages ====================
    ScheduleBookingPage: {
        name: "ScheduleBookingPage",
        dashboardComponent: false,
        entryPoint: () => import("./pages/schedule-booking/ScheduleBooking" /* webpackChunkName: "schedule-booking" */)
    },
    ScheduledAirflowTaskConfigPage: {
        name: "Scheduled Task Config",
        dashboardComponent: false,
        entryPoint: () => import("./pages/scheduled-task-airflow/AirflowTaskConfigurator" /* webpackChunkName: "scheduled-airflow-task-config-page" */)
    },
    DatasetPage: {
        name: "Datasets",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/dataset-window" /* webpackChunkName: "dataset-window" */)
    },
    DashboardReportsPage: {
        name: "DashboardReportsPage",
        dashboardComponent: false,
        entryPoint: () => import("./pages/dashboard-report/DashboardReportPage" /* webpackChunkName: "dashboard-reports-page" */)
    },

    // ========= Dev ==============
    ReactExample: {
        name: "DEV TEST",
        dashboardComponent: true,
        entryPoint: () => import("./dashboard/test/App" /* webpackChunkName: "react-example" */)
    },
    DevSandbox: {
        name: "Dev Sandbox",
        dashboardComponent: false,
        entryPoint: () => import("./dashboard/dataset-window" /* webpackChunkName: "dev-sandbox" */)
    },
};

export function loadComponentModule(type: keyof IComponentTypeMappings) {
    if (!(type in ComponentTypeMappings)) {
        console.warn("Can't map unknown component type: " + type)
        return undefined;
    }

    return ComponentTypeMappings[type].entryPoint()
}

export function getDashboardComponentTypes(): ComponentTypeWithID[] {
    return objectPropertiesToArray(ComponentTypeMappings).filter((type) => type.dashboardComponent == true) as ComponentTypeWithID[];
}
